import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Input, Button, Tooltip } from "antd";
import removeSVG from "../../assets/close.svg";
import addSVG from "../../assets/add_item.svg";
import trancheTableColumns from "./json/trancheTableColumns.json";
import { useEditableCell } from "../../hooks/useEditableTableCell.js";
import { calculateTrancheTableValues } from "./_10b51utils.js";
import {
  emdash,
  hasHoverText,
  RadioSelector,
  toDollar,
  trancheOptionAstericksMessage,
} from "../../utils/Constants/systemSetting";
import dayjs from "dayjs";

export default function TrancheTable({
  trancheTableValues,
  setTrancheTableValues,
  calculatorPageSelected,
  generalInfoParams = {},
  optionsCalculatorValues = {},
  currentAwardValues = {},
  hidden = false,
  trancheScheduleSelected = "grant1",
  prevTrancheScheduleSelected = "grant1",
  taxTableParams = [],
  includeTaxes = false,
}) {
  const renderEditableCell = useEditableCell();

  const [calculatedValuesUpdateNeeded, setCalculatedValuesUpdateNeeded] =
    useState(true);

  // Initialization function to populate the first tranche values
  useEffect(() => {
    if (!trancheTableValues?.length) {
      const trancheTableDefaultValues = Object.fromEntries(
        Object.values({ ...trancheTableColumns }).map((row) => [
          row.key,
          row.key === "ID" || row.key === "key" ? "Tranche 1" : "",
        ]),
      );
      setTrancheTableValues([trancheTableDefaultValues]);
    }
  }, [trancheTableValues, setTrancheTableValues]);

  // Function for updating the tranche values
  const updateTrancheParams = useCallback(
    (ID, record, newValue) => {
      if (!trancheTableValues) return;

      const trancheIndex = trancheTableValues.findIndex(
        (item) => item.ID === ID,
      );

      const newTrancheValues = [...trancheTableValues];
      const updatedTrancheValues = {
        ...trancheTableValues[trancheIndex],
        [record.key]: newValue,
      };
      newTrancheValues[trancheIndex] = updatedTrancheValues;

      setTrancheTableValues(newTrancheValues);
      setCalculatedValuesUpdateNeeded(true);
    },
    [setTrancheTableValues, trancheTableValues],
  );

  const currentOptionCalculatorValues = useMemo(() => {
    return optionsCalculatorValues.find(
      (item) => item.ID === trancheScheduleSelected.replace("Grant ", "grant"),
    );
  }, [optionsCalculatorValues, trancheScheduleSelected]);

  const currentAwardCalculatorValues = useMemo(() => {
    return currentAwardValues.find(
      (item) => item.ID === trancheScheduleSelected.replace("Grant ", "grant"),
    );
  }, [currentAwardValues, trancheScheduleSelected]);

  // Trigger Updates for calculated values within the Tranche Table based on other tables
  useMemo(() => {
    if (
      currentOptionCalculatorValues ||
      generalInfoParams[0] ||
      trancheScheduleSelected ||
      taxTableParams ||
      trancheScheduleSelected !== prevTrancheScheduleSelected
    ) {
      setCalculatedValuesUpdateNeeded(true);
    }
  }, [
    generalInfoParams,
    taxTableParams,
    currentOptionCalculatorValues,
    trancheScheduleSelected,
    prevTrancheScheduleSelected,
  ]);

  const getDividendPaymentDates = (
    nextDividendDate,
    vestDate,
    frequencyMonths,
  ) => {
    const dates = [];
    const end = dayjs(vestDate);

    for (
      let date = dayjs(nextDividendDate);
      date.isBefore(end) || date.isSame(end);
      date = date.add(frequencyMonths, "month")
    ) {
      dates.push(date);
    }

    return dates;
  };

  // Function For calculating Option Values in the Tranche Table
  useEffect(() => {
    if (!trancheTableValues?.length) return;
    const dividendColumns = [
      {
        title: "Dividend Date",
        dataIndex: "date",
        key: "date",
        align: "center",
      },
      {
        title: "Dividend Eligible Units",
        dataIndex: "activeUnits",
        key: "activeUnits",
        render: (item) => item.toLocaleString(),
        align: "center",
      },
      {
        title: "Projected Dividend/Share",
        dataIndex: "projectedDividend",
        key: "projectedDividend",
        render: (item, record) => {
          return record.date === "Total"
            ? `~${toDollar(item || 0, 2, true)}`
            : toDollar(item, 2, true);
        },
        align: "center",
      },
      {
        title: "Present Value",
        dataIndex: "projectedDividendPV",
        key: "projectedDividendPV",
        render: (item, record) => {
          return record.date === "Total"
            ? `~${toDollar(item || 0, 2, true)}`
            : toDollar(item, 2, true);
        },
        align: "center",
      },
      {
        title: "Payment",
        dataIndex: "payment",
        key: "payment",
        render: (item) => toDollar(item, 0, true),
        align: "center",
      },
    ];

    if (calculatedValuesUpdateNeeded) {
      const newTrancheTableValues = [...trancheTableValues];
      if (calculatorPageSelected === "Options Schedule") {
        trancheTableValues.forEach((trancheRowValues, awardIndex) => {
          const calaculatedTrancheTableValues = calculateTrancheTableValues(
            {
              ...generalInfoParams?.[0],
              ...currentOptionCalculatorValues,
              ...trancheRowValues,
            },
            taxTableParams,
          );
          newTrancheTableValues[awardIndex] = {
            ...trancheRowValues,
            ...calaculatedTrancheTableValues,
          };
        });
      } else if (calculatorPageSelected === "RSU Schedule") {
        let cumulativeUnits = 0;
        const shortTermTaxRate =
          (taxTableParams.find((item) => item.ID === "Short-Term")?.totalTax ||
            0) / 100;
        // To be used as table advances to detect short and long term captial gains
        const longTermTaxRate =
          (taxTableParams.find((item) => item.ID === "Long-Term")?.totalTax ||
            0) / 100;

        // Step 1: build full dividend schedule outside the forEach
        const nextDividendDate = generalInfoParams?.[0]?.dividendDate;
        // dividendFrequencyMonths: 1 for month, 3 for quarter, 12 for annual:
        const dividendFrequencyMonths =
          generalInfoParams?.[0]?.dividendFrequency === "Monthly" ? 1 : 3;
        const exactDividendAmount = generalInfoParams?.[0]?.dividendAmount;
        const dividendGrowthRate =
          generalInfoParams?.[0]?.dividendGrowthRate / 100;
        const lastVestDate = dayjs(
          Math.max(
            ...trancheTableValues.map((t) => dayjs(t.exerciseDate).valueOf()),
          ),
        );
        const allPaymentDates = getDividendPaymentDates(
          nextDividendDate,
          lastVestDate,
          dividendFrequencyMonths,
        );
        const today = dayjs();

        let remainingUnitsCumulative = 0;
        let remainingUnitsCumulativeAfterTax = 0;
        let remainingFullValueCumulative = 0;
        let remainingFullValueAfterTaxCumulative = 0;
        // Step 2: inside the forEach, sum up payments relevant to each tranche
        trancheTableValues.forEach((trancheRowValues, awardIndex) => {
          const remainingUnits = trancheRowValues?.initialUnits;
          const vestDate = dayjs(trancheRowValues.exerciseDate);

          const filteredPaymentDates = allPaymentDates.filter((dividendDate) =>
            vestDate.isAfter(dividendDate),
          );

          const dividendSchedule = filteredPaymentDates.map((date, index) => {
            const activeUnits = trancheTableValues.reduce((acc, tranche) => {
              const trancheDate = dayjs(tranche.exerciseDate);
              if (
                vestDate.isAfter(today) &&
                vestDate.isAfter(date) &&
                (trancheDate.isBefore(date) || trancheDate.isSame(date)) &&
                trancheDate.isAfter(today)
              ) {
                return acc + Number(tranche.remainingUnits);
              }
              return acc;
            }, 0);

            const yearsFromNow = date.diff(today, "day") / 365;
            const projectedDividend =
              exactDividendAmount *
              Math.pow(1 + dividendGrowthRate, yearsFromNow);

            const projectedDividendPV = !isNaN(
              currentAwardCalculatorValues?.riskFreeRate,
            )
              ? parseFloat(
                  projectedDividend *
                    Math.exp(
                      -(currentAwardCalculatorValues?.riskFreeRate / 100) *
                        yearsFromNow,
                    ),
                )
              : projectedDividend;
            const payment = activeUnits * projectedDividendPV;

            return {
              key: index,
              date: date.format("M/D/YYYY"),
              activeUnits,
              projectedDividend,
              projectedDividendPV,
              payment,
            };
          });

          const dividendsForegone = vestDate.isAfter(today)
            ? dividendSchedule
                .filter(
                  (row) =>
                    dayjs(row.date).isBefore(vestDate) ||
                    dayjs(row.date).isSame(vestDate),
                )
                .reduce((acc, row) => acc + row.payment, 0)
            : 0;

          const remainingFullValue =
            remainingUnits * (generalInfoParams?.[0]?.sharePrice || 0);

          // Old method - calculating tax on the value of the award
          // const remainingFullValueAfterTax = Math.max(
          //   remainingFullValue * (1 - shortTermTaxRate),
          //   0,
          // );

          remainingUnitsCumulative += Number(remainingUnits);
          remainingUnitsCumulativeAfterTax += Math.round(
            Number(remainingUnits) * (1 - shortTermTaxRate),
          );

          const remainingFullValueAfterTax =
            remainingUnitsCumulativeAfterTax *
            (generalInfoParams?.[0]?.sharePrice || 0);

          remainingFullValueCumulative += remainingFullValue;
          remainingFullValueAfterTaxCumulative += remainingFullValueAfterTax;

          const exerciseDate =
            !trancheRowValues?.exerciseDate && trancheRowValues?.trancheDate
              ? dayjs(trancheRowValues?.trancheDate).add(1, "day").format()
              : trancheRowValues?.exerciseDate
                ? trancheRowValues?.exerciseDate
                : "";

          const updatedTrancheRowValues = {
            ...trancheRowValues,
            remainingUnits,
            remainingFullValue,
            remainingFullValueAfterTax,
            remainingUnitsCumulative,
            remainingUnitsCumulativeAfterTax,
            remainingFullValueCumulative,
            remainingFullValueAfterTaxCumulative,
            exerciseDate,
            dividendsForegone,
          };

          const trancheDividendSchedule = vestDate.isAfter(today)
            ? dividendSchedule.filter(
                (row) =>
                  dayjs(row.date).isBefore(vestDate) ||
                  dayjs(row.date).isSame(vestDate),
              )
            : [];
          if (trancheDividendSchedule.length > 0) {
            const totalUnits = trancheDividendSchedule.reduce(
              (acc, row) => acc + row.activeUnits,
              0,
            );
            const avgDividend =
              trancheDividendSchedule.reduce(
                (acc, row) => acc + row.projectedDividend,
                0,
              ) / trancheDividendSchedule.length;
            const avgDividendPV =
              trancheDividendSchedule.reduce(
                (acc, row) => acc + row.projectedDividendPV,
                0,
              ) / trancheDividendSchedule.length;

            trancheDividendSchedule.push({
              date: "Total",
              activeUnits: totalUnits, // doesn't really sum meaningfully
              projectedDividend: avgDividend,
              projectedDividendPV: avgDividendPV,
              payment: trancheDividendSchedule.reduce(
                (acc, row) => acc + row.payment,
                0,
              ),
            });
          }

          newTrancheTableValues[awardIndex] = {
            ...updatedTrancheRowValues,
            dividendsForegone: {
              value: dividendsForegone,
              hoverText:
                // If outputting data for default values, set this to this string instead:
                // "Change a value in this table to view the dividend schedule",
                trancheDividendSchedule.length ? (
                  <Table
                    dataSource={trancheDividendSchedule}
                    columns={dividendColumns}
                    pagination={false}
                    size="small"
                  />
                ) : (
                  <p>Already vested, no dividends foregone</p>
                ),
              hoverTextOverlayStyle: { maxWidth: 500 },
            },
          };
        });
      }
      setTrancheTableValues([...newTrancheTableValues]);
      setCalculatedValuesUpdateNeeded(false);
    }
  }, [
    calculatorPageSelected,
    setTrancheTableValues,
    generalInfoParams,
    taxTableParams,
    optionsCalculatorValues,
    trancheTableValues,
    calculatedValuesUpdateNeeded,
    currentOptionCalculatorValues,
    currentAwardCalculatorValues,
  ]);

  // Function for removing a tranche row (X button on frontend)
  const handleRemove = useCallback(
    (key) => {
      setTrancheTableValues((prevData) =>
        prevData.filter((item) => item.ID !== key),
      );
    },
    [setTrancheTableValues],
  );

  // Function for adding an tranche row (+ button on frontend)
  const handleAdd = useCallback(() => {
    if (!trancheTableValues) return;

    const addTrancheRow = (grantID) => {
      const newValues = Object.fromEntries(
        Object.values(trancheTableColumns).map((row) => [
          row.key,
          row.key === "ID" || row.key === "key" ? grantID : "",
        ]),
      );
      setTrancheTableValues((prev) => [...prev, newValues]);
    };

    const maxColumnNumber = Math.max(
      ...trancheTableValues.map((item) =>
        parseInt(item.key.replace("Tranche ", "")),
      ),
    );
    addTrancheRow(`Tranche ${maxColumnNumber + 1}`);
  }, [trancheTableValues, setTrancheTableValues]);

  // Define each of the Tranche columns based on trancheTableColumns.json
  const defaultColumns = [
    {
      title: "Add\u00A0Tranche",
      key: "addTranche",
      align: "center",
    },
    {
      title: "Remove",
      key: "removeTranche",
      align: "center",
    },
    {
      title: "Name",
      dataIndex: "ID",
      align: "center",
      key: "ID",
      render: (item) => (item ? item.replace(" ", "\u00A0") : item),
    },
    ...Object.values(trancheTableColumns)

      .map((col) => ({
        title: col.title,
        key: col.key,
        align: "center",
        dataType: col.dataType,
        defaultValue: col.defaultValue,
        hideFromUser: col.hideFromUser,
        isCalculatedValue: col.isCalculatedValue,
        isTrancheColumn: true,
        optionsCalculatorOnly: col.optionsCalculatorOnly,
        RSUcalculatorOnly: col.RSUcalculatorOnly,
        isPreTaxValue: col.isPreTaxValue,
        isAfterTaxValue: col.isAfterTaxValue,
        hasHoverText: col.hasHoverText,
      })),
  ];
  const [trancheColumns, setTrancheColumns] = useState([...defaultColumns]);

  // Function to add a render function to each column (to keep each ref in memory current)
  const [currentEditedCell, setCurrentEditedCell] = useState("");
  const trancheTableColumnsRendered = useMemo(
    () =>
      trancheColumns
        .filter((col) => !col.hideFromUser)
        .filter(
          (col) =>
            !includeTaxes || !(col?.isPreTaxValue && !col?.isAfterTaxValue),
        )
        .filter(
          (col) =>
            includeTaxes || !(!col?.isPreTaxValue && col?.isAfterTaxValue),
        )
        .filter(
          (col) =>
            !col.optionsCalculatorOnly ||
            !(
              col.optionsCalculatorOnly &&
              calculatorPageSelected !== "Options Schedule"
            ),
        )
        .filter(
          (col) =>
            !col.RSUcalculatorOnly ||
            !(
              col.RSUcalculatorOnly && calculatorPageSelected !== "RSU Schedule"
            ),
        )
        .map((col) => {
          if (col.key === "addTranche") {
            col = {
              ...col,
              render: (_, record) => (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    cursor="pointer"
                    type="link"
                    onClick={() => handleAdd(record.ID)}
                  >
                    <img src={addSVG} alt="add" width={16} />
                  </Button>
                </div>
              ),
            };
          }
          if (col.key === "removeTranche") {
            col = {
              ...col,
              render: (_, record) => (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    cursor="pointer"
                    type="link"
                    onClick={() => handleRemove(record.ID)}
                  >
                    <img src={removeSVG} alt="remove" width={11} />
                  </Button>
                </div>
              ),
            };
          }

          if (col.isTrancheColumn) {
            col = {
              ...col,
              render: (text, record) => {
                const currentTrancheValues = trancheTableValues?.find(
                  (item) => item.ID === record.ID,
                );

                const cellKey = `${col.key}-${record.ID}`;
                const isCurrentEditedCell = currentEditedCell === cellKey;

                const currentAssignedValue = currentTrancheValues?.[col.key];

                const value =
                  currentAssignedValue ||
                  (isCurrentEditedCell
                    ? currentAssignedValue
                    : record.defaultValue !== "" && col.key === "Tranche 1"
                      ? record.defaultValue
                      : (col.key === "killAmountAfterTax" ||
                            col.key === "killAmount" ||
                            col.key === "killPercentage" ||
                            col.key === "intrinsicValue" ||
                            col.key === "intrinsicValueAfterTax" ||
                            col?.isAfterTaxValue) &&
                          record?.remainingFullValue
                        ? col.key === "killAmountAfterTax" ||
                          col.key === "killPercentage" ||
                          col.key === "killAmount"
                          ? "*"
                          : emdash
                        : "");

                return renderEditableCell(
                  value,
                  col,
                  record.ID,
                  col.dataType,
                  updateTrancheParams,
                  setCurrentEditedCell,
                  { width: "100%" },
                );
              },
            };
          }
          return col;
        }),
    [
      calculatorPageSelected,
      trancheColumns,
      handleAdd,
      handleRemove,
      updateTrancheParams,
      renderEditableCell,
      trancheTableValues,
      currentEditedCell,
      includeTaxes,
    ],
  );

  const astericksExists = useMemo(() => {
    return (
      calculatorPageSelected === "Options Schedule" &&
      trancheTableValues.some((item) => item?.initialUnits && !item?.killAmount)
    );
  }, [calculatorPageSelected, trancheTableValues]);

  // Finally, return the table with editable and calculate-able values
  return hidden ? (
    <></>
  ) : (
    <>
      <Table
        columns={trancheTableColumnsRendered}
        dataSource={trancheTableValues}
        pagination={false}
        size="small"
      />
      {astericksExists && (
        <div style={{ marginTop: 10, marginLeft: 10 }}>
          {trancheOptionAstericksMessage}
        </div>
      )}
    </>
  );
}
